/**
 * CloseButton Component
 * Unified close/dismiss button with modern styling and animations
 * Works across both desktop and mobile interfaces
 */

import React from 'react';
import './CloseButton.css';

/**
 * Props for CloseButton component
 */
export interface CloseButtonProps {
  /** Callback when button is clicked */
  onClick: () => void;
  /** Accessible label for screen readers */
  ariaLabel?: string;
  /** Optional CSS class name for additional styling */
  className?: string;
  /** Size variant - defaults to 'medium' */
  size?: 'small' | 'medium' | 'large';
  /** Visual variant - defaults to 'default' */
  variant?: 'default' | 'minimal' | 'prominent';
}

/**
 * CloseButton component
 * Renders a consistent close/dismiss button with hover and click animations
 * 
 * @param props - Component props
 * @returns Close button component
 */
export const CloseButton: React.FC<CloseButtonProps> = ({
  onClick,
  ariaLabel = 'Close',
  className = '',
  size = 'medium',
  variant = 'default',
}) => {
  return (
    <button
      className={`close-button close-button--${size} close-button--${variant} ${className}`}
      onClick={onClick}
      aria-label={ariaLabel}
      type="button"
    >
      <svg
        className="close-button__icon"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <path
          className="close-button__icon-line close-button__icon-line--1"
          d="M18 6L6 18"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          className="close-button__icon-line close-button__icon-line--2"
          d="M6 6L18 18"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
};
